import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../helpers/base_url";

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{
    type: "idle" | "success" | "error";
    message: string;
  }>({ type: "idle", message: "" });

  const isEmailValid = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus({ type: "idle", message: "" });

    if (!email.trim() || !isEmailValid(email)) {
      setStatus({
        type: "error",
        message: "Please enter a valid email address.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(buildApiUrl("/api/auth/forgot-password"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      // Regardless of whether the email exists, backend returns 2xx with generic message
      if (response.ok) {
        setStatus({
          type: "success",
          message:
            "If an account with that email exists, we’ve sent a password reset link.",
        });
      } else {
        const data = await response.json().catch(() => null);
        setStatus({
          type: "error",
          message:
            data?.message ||
            "We couldn’t process your request right now. Please try again.",
        });
      }
    } catch (error) {
      setStatus({
        type: "error",
        message: "Network error. Please check your connection and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, #4E1C81 0, #4E1C81 35%, #020617 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "80px 16px 40px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 480,
          background:
            "linear-gradient(145deg, #4E1C81 0%, #AB68C5 100%)",
          borderRadius: 24,
          padding: 32,
          boxShadow: "0 30px 80px rgba(15,23,42,0.8)",
          border: "1px solid rgba(171,104,197,0.7)",
          color: "#e5e7eb",
        }}
      >
        <button
          type="button"
          onClick={() => navigate("/login")}
          style={{
            border: "none",
            background: "rgba(15,23,42,0.5)",
            color: "rgba(226,232,240,0.9)",
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 20,
            cursor: "pointer",
            padding: "6px 10px",
            borderRadius: 999,
          }}
        >
          <span style={{ fontSize: 16 }}>←</span> Back to login
        </button>

        <h1
          style={{
            fontSize: 28,
            fontWeight: 800,
            marginBottom: 8,
            color: "#eef2ff",
          }}
        >
          Forgot your password?
        </h1>
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.7,
            color: "rgba(199,210,254,0.95)",
            marginBottom: 20,
          }}
        >
          Enter the email linked to your EventNova account and we&apos;ll send
          you a secure link to reset your password.
        </p>

        {status.type === "success" && (
          <div
            style={{
              background: "rgba(22,163,74,0.18)",
              color: "#bbf7d0",
              borderRadius: 12,
              padding: "10px 14px",
              border: "1px solid rgba(34,197,94,0.55)",
              fontSize: 13,
              marginBottom: 16,
              fontWeight: 500,
            }}
          >
            {status.message}
          </div>
        )}

        {status.type === "error" && (
          <div
            style={{
              background: "rgba(248,113,113,0.16)",
              color: "#fecaca",
              borderRadius: 12,
              padding: "10px 14px",
              border: "1px solid rgba(248,113,113,0.7)",
              fontSize: 13,
              marginBottom: 16,
              fontWeight: 500,
            }}
          >
            {status.message}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 18 }}>
            <label
              style={{
                display: "block",
                marginBottom: 6,
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: 0.3,
                textTransform: "uppercase",
                color: "#c7d2fe",
              }}
            >
              Email address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid rgba(129,140,248,0.9)",
                background: "rgba(15,23,42,0.9)",
                color: "#e5e7eb",
                fontSize: 14,
                outline: "none",
              }}
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !email.trim()}
            style={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: 12,
              border: "none",
              fontSize: 15,
              fontWeight: 700,
              color: "#f9fafb",
              background: isSubmitting || !email.trim()
                ? "rgba(171,104,197,0.5)"
                : "linear-gradient(135deg, #4E1C81 0%, #AB68C5 100%)",
              cursor:
                isSubmitting || !email.trim() ? "not-allowed" : "pointer",
              boxShadow:
                isSubmitting || !email.trim()
                  ? "none"
                  : "0 18px 40px rgba(15,23,42,0.9)",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
          >
            {isSubmitting ? "Sending reset link..." : "Send reset link"}
          </button>

          <p
            style={{
              marginTop: 14,
              fontSize: 12,
              color: "rgba(191,219,254,0.9)",
              lineHeight: 1.6,
            }}
          >
            For security, we&apos;ll only say if the reset email was sent — even
            if the email is not registered.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;


